<template>
  <v-app class="rounded rounded-md">
    <TopBar @toggle-drawer="drawer = !drawer" />
    <TopMenu v-model="drawer" />
    
    <v-main class="bg-grey-lighten-4">
      <v-container class="py-8" max-width="600">
        <h2 class="text-h5 font-weight-bold mb-6 text-center">マイページ</h2>
        
        <v-card class="mx-auto elevation-2 rounded-lg" v-if="authorize && user">
          <v-card-text class="pa-6">
            <v-list lines="two" class="bg-transparent">
              <v-list-item>
                <template v-slot:prepend>
                  <v-avatar color="primary" size="64" class="mr-4 text-white text-h5 font-weight-bold">
                    {{ user.name ? user.name.charAt(0).toUpperCase() : 'U' }}
                  </v-avatar>
                </template>
                <v-list-item-title class="text-h5 font-weight-bold">{{ user.name }}</v-list-item-title>
                <v-list-item-subtitle class="mt-1 text-subtitle-1">@{{ user.user_id }}</v-list-item-subtitle>
              </v-list-item>

              <v-divider class="my-4"></v-divider>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="grey-darken-1" class="mr-3">mdi-email</v-icon>
                </template>
                <v-list-item-title class="font-weight-medium">メールアドレス</v-list-item-title>
                <v-list-item-subtitle class="mt-1">{{ user.mail }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>

            <v-card-actions class="mt-4 justify-center">
              <v-btn color="error" variant="outlined" class="px-6 font-weight-bold" @click="logout">
                <v-icon start>mdi-logout</v-icon>
                ログアウト
              </v-btn>
            </v-card-actions>
          </v-card-text>
        </v-card>

        <!-- お気に入り・自店舗タブ -->
        <v-card class="mx-auto mt-6 elevation-2 rounded-lg" v-if="authorize && user">
          <v-tabs v-model="tab" color="primary" grow>
            <v-tab value="favorites">
              <v-icon start>mdi-heart</v-icon>お気に入り
            </v-tab>
            <v-tab value="owns">
              <v-icon start>mdi-store</v-icon>自店舗
            </v-tab>
            <v-tab value="own_teams">
              <v-icon start>mdi-account-group</v-icon>自チーム
            </v-tab>
          </v-tabs>
          
          <v-window v-model="tab">
            <v-window-item value="favorites">
              <v-list lines="two" v-if="favorite_stores.length > 0">
                <v-list-item 
                  v-for="store in favorite_stores" 
                  :key="store.sports_store_id"
                  :to="{ name: 'sportsstoredetail', params: {sportsStoreId: store.sports_store_id} }"
                >
                  <template v-slot:prepend>
                    <v-icon color="pink">mdi-heart</v-icon>
                  </template>
                  <v-list-item-title class="font-weight-bold">{{ store.store_name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ store.address_prefecture }}{{ store.address_city_town_village }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
              <div v-else class="text-center py-6 text-grey">お気に入りの店舗はありません</div>
            </v-window-item>
            
            <v-window-item value="owns">
              <v-list lines="two" v-if="own_stores.length > 0">
                <v-list-item 
                  v-for="store in own_stores" 
                  :key="store.sports_store_id"
                  :to="{ name: 'sportsstoredetail', params: {sportsStoreId: store.sports_store_id} }"
                >
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-store</v-icon>
                  </template>
                  <v-list-item-title class="font-weight-bold">{{ store.store_name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ store.address_prefecture }}{{ store.address_city_town_village }}</v-list-item-subtitle>
                  <template v-slot:append>
                    <v-btn size="small" icon variant="text" color="primary" @click.prevent="$router.push({name: 'update-sportsstoreedit', params: {sportsStoreId: store.sports_store_id}})">
                      <v-icon>mdi-pencil</v-icon>
                      <v-tooltip activator="parent" location="top">編集する</v-tooltip>
                    </v-btn>
                    <v-btn size="small" icon variant="text" color="error" @click.prevent="deleteStore(store.sports_store_id, store.store_name)">
                      <v-icon>mdi-delete</v-icon>
                      <v-tooltip activator="parent" location="top">削除する</v-tooltip>
                    </v-btn>
                  </template>
                </v-list-item>
              </v-list>
              <div v-else class="text-center py-6 text-grey">登録した自店舗はありません</div>
              <v-card-actions class="justify-center border-t">
                <v-btn color="primary" variant="flat" to="/sportsstoreedit">
                  <v-icon start>mdi-plus</v-icon>新しい店舗を追加する
                </v-btn>
              </v-card-actions>
            </v-window-item>

            <v-window-item value="own_teams">
              <v-list lines="two" v-if="own_teams.length > 0">
                <v-list-item 
                  v-for="team in own_teams" 
                  :key="team.team_club_circle_id"
                  :to="{ name: 'teamclubcircledetail', params: {teamClubCircleId: team.team_club_circle_id} }"
                >
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-account-group</v-icon>
                  </template>
                  <v-list-item-title class="font-weight-bold">{{ team.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ team.activity_location_prefecture }}{{ team.activity_location }}</v-list-item-subtitle>
                  <template v-slot:append>
                    <v-btn size="small" icon variant="text" color="primary" @click.prevent="$router.push({name: 'update-teamclubcircleedit', params: {teamClubCircleId: team.team_club_circle_id}})">
                      <v-icon>mdi-pencil</v-icon>
                      <v-tooltip activator="parent" location="top">編集する</v-tooltip>
                    </v-btn>
                    <v-btn size="small" icon variant="text" color="error" @click.prevent="deleteTeam(team.team_club_circle_id, team.name)">
                      <v-icon>mdi-delete</v-icon>
                      <v-tooltip activator="parent" location="top">削除する</v-tooltip>
                    </v-btn>
                  </template>
                </v-list-item>
              </v-list>
              <div v-else class="text-center py-6 text-grey">登録した自チームはありません</div>
              <v-card-actions class="justify-center border-t">
                <v-btn color="primary" variant="flat" to="/teamclubcircleedit">
                  <v-icon start>mdi-plus</v-icon>新しいチームを追加する
                </v-btn>
              </v-card-actions>
            </v-window-item>
          </v-window>
        </v-card>

        <v-card v-else-if="!authorize" class="mx-auto text-center pa-6 elevation-2 rounded-lg">
          <v-icon color="warning" size="64" class="mb-4">mdi-alert-circle</v-icon>
          <h3 class="text-h6 mb-2">ログインが必要です</h3>
          <p class="text-body-2 text-grey-darken-1 mb-6">マイページを表示するにはログインしてください。</p>
          <v-btn color="primary" to="/login" class="px-8">ログイン画面へ</v-btn>
        </v-card>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import TopBar from '@/components/TopBar.vue'
import TopMenu from '@/components/TopMenu.vue'
import axios from 'axios'

export default {
  name: 'MyPage',
  components: {
    TopBar,
    TopMenu,
  },
  data: () => ({
    drawer: false,
    authorize: false,
    user: null,
    tab: 'favorites',
    favorite_stores: [],
    own_stores: [],
    own_teams: [],
  }),
  created: function () {
    this.checkLogin()
  },
  methods: {
    checkLogin() {
      axios.get(
        process.env.VUE_APP_API_URL + '/api/logincheck',
        { headers: { "Authorization": JSON.stringify(this.$store.getters.getToken) } }
      ).then(response => {
        if (response.status == 200 && response.data.user) {
          this.user = response.data.user
          this.authorize = true
          this.fetchMyPageData()
        } else {
          this.authorize = false
        }
      }).catch(() => {
        this.authorize = false
      })
    },
    fetchMyPageData() {
      axios.get(
        process.env.VUE_APP_API_URL + '/api/mypage_data',
        { headers: { "Authorization": JSON.stringify(this.$store.getters.getToken) } }
      ).then(response => {
        if (response.status == 200) {
          this.favorite_stores = response.data.favorite_stores
          this.own_stores = response.data.own_stores
          this.own_teams = response.data.own_teams
        }
      })
    },
    logout() {
      // ストアのトークンを削除してトップページへ遷移
      this.$store.dispatch('saveToken', null)
      this.$router.push('/login')
    },
    deleteStore(storeId, storeName) {
      if (confirm(`店舗「${storeName}」を本当に削除しますか？この操作は取り消せません。`)) {
        axios.post(
          process.env.VUE_APP_API_URL + '/sportsstore/delete/' + storeId,
          {},
          { headers: { "Authorization": JSON.stringify(this.$store.getters.getToken) } }
        ).then(response => {
          if (response.status == 200) {
            alert('店舗を削除しました。');
            this.fetchMyPageData(); // リストを再取得
          }
        }).catch(err => {
          console.error(err);
          alert(err.response?.data?.error || '削除に失敗しました。');
        });
      }
    },
    deleteTeam(teamId, teamName) {
      if (confirm(`チーム「${teamName}」を本当に削除しますか？この操作は取り消せません。`)) {
        axios.post(
          process.env.VUE_APP_API_URL + '/teamclubcircle/delete/' + teamId,
          {},
          { headers: { "Authorization": JSON.stringify(this.$store.getters.getToken) } }
        ).then(response => {
          if (response.status == 200) {
            alert('チームを削除しました。');
            this.fetchMyPageData(); // リストを再取得
          }
        }).catch(err => {
          console.error(err);
          alert(err.response?.data?.error || '削除に失敗しました。');
        });
      }
    }
  },
}
</script>
